import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PpkService, PpkStatusResponse, DownloaderFile, PpkConvertFile, PpkConvertFilesResponse } from '../../services/ppk.service';
import { UploadService } from '../../services/upload.service';
import { PermissionService } from '../../services/permission.service';
import { AcknowledgeDialogComponent } from '../acknowledge-dialog/acknowledge-dialog.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { SelectDownloaderFileDialogComponent } from './select-downloader-file-dialog/select-downloader-file-dialog.component';
import { PpkStudioHelpDialogComponent } from './ppk-studio-help-dialog/ppk-studio-help-dialog.component';

interface PpkFileState {
  roverObsName?: string;
  roverObsUrl?: string;
  baseObsName?: string;
  baseObsUrl?: string;
  roverNavName?: string;
  roverNavUrl?: string;
  resultPosUrl?: string;
}

@Component({
  selector: 'app-ppk-studio',
  templateUrl: './ppk-studio.component.html',
  styleUrls: ['./ppk-studio.component.scss']
})
export class PpkStudioComponent implements OnInit, OnDestroy {

  title = 'PPK Studio';

  isDemo = false;
  isDownloaderPro = false;

  // left panel state
  roverLoading = false;
  baseLoading = false;
  navLoading = false;
  processLoading = false;

  roverProgress: number | null = null;
  baseProgress: number | null = null;
  navProgress: number | null = null;

  statusText = '';
  ppkRunStatus: 'IDLE' | 'RUNNING' | 'SUCCESS' | 'FAILED' | '' = 'IDLE';
  quickStartExpanded = false;

  // Remember what the user uploaded to decide whether NAV is required
  roverInputKind: 'ubx' | 'obs' | null = null;
  baseInputKind: 'rtcm3' | 'obs' | null = null;

  files: PpkFileState = {};

  private statusPollHandle: any;
  private exportPollHandle: any;
  private exportPollStartAt = 0;
  private postUploadRoverPollHandle: any;
  private postUploadBasePollHandle: any;
  private postUploadRoverWarned = false;
  private postUploadBaseWarned = false;

  exportLoading = false;
  exportStatus: 'IDLE' | 'RUNNING' | 'SUCCESS' | 'FAILED' = 'IDLE';
  exportStatusText = '';
  exportedFiles: PpkConvertFile[] = [];

  get canExport(): boolean {
    return !!this.files.resultPosUrl && !this.isDemo && !this.exportLoading && this.exportStatus !== 'RUNNING';
  }

  constructor(
    private readonly ppk: PpkService,
    private readonly uploader: UploadService,
    private readonly perms: PermissionService,
    private readonly snackBar: MatSnackBar,
    private readonly dialog: MatDialog
  ) {
    this.perms.ensureFresh();
    this.isDemo = this.perms.has('demo');
    this.isDownloaderPro = this.perms.has('downloader-pro');
  }

  openHelp(): void {
    this.dialog.open(PpkStudioHelpDialogComponent, {
      width: '900px',
      maxWidth: '95vw'
    });
  }

  toggleQuickStart(): void {
    this.quickStartExpanded = !this.quickStartExpanded;
  }

  private isLikelyRoverConverting(): boolean {
    return (this.roverInputKind === 'ubx') && !this.files.roverObsName;
  }

  private isLikelyBaseConverting(): boolean {
    return (this.baseInputKind === 'rtcm3') && !this.files.baseObsName;
  }

  get stepRoverStatus(): { label: string; tone: 'muted' | 'info' | 'success' | 'danger' } {
    if (this.roverLoading) return { label: 'Uploading...', tone: 'info' };
    if (this.files.roverObsName) return { label: 'Ready', tone: 'success' };
    return { label: 'Not uploaded', tone: 'muted' };
  }

  get stepBaseStatus(): { label: string; tone: 'muted' | 'info' | 'success' | 'danger' } {
    if (this.baseLoading) return { label: 'Uploading...', tone: 'info' };
    if (this.files.baseObsName) return { label: 'Ready', tone: 'success' };
    return { label: 'Not uploaded', tone: 'muted' };
  }

  get stepNavStatus(): { label: string; tone: 'muted' | 'info' | 'success' | 'danger' } {
    if (this.navLoading) return { label: 'Uploading...', tone: 'info' };
    if (this.files.roverNavName) return { label: 'Ready', tone: 'success' };
    return { label: 'Not uploaded', tone: 'muted' };
  }

  get stepProcessStatus(): { label: string; tone: 'muted' | 'info' | 'success' | 'danger' } {
    if (this.files.resultPosUrl) return { label: 'Completed', tone: 'success' };
    if (this.processLoading || this.ppkRunStatus === 'RUNNING') return { label: 'Processing in backend...', tone: 'info' };
    if (this.ppkRunStatus === 'FAILED') return { label: 'Failed', tone: 'danger' };
    if (this.canProcess) return { label: 'Waiting for process', tone: 'success' };
    return { label: 'Not ready', tone: 'muted' };
  }

  get stepExportStatus(): { label: string; tone: 'muted' | 'info' | 'success' | 'danger' } {
    if (!this.files.resultPosUrl) return { label: 'Waiting for PPK result...', tone: 'muted' };
    if (this.exportLoading || this.exportStatus === 'RUNNING') return { label: 'Exporting...', tone: 'info' };
    if (this.exportStatus === 'FAILED') return { label: 'Failed (retry Export)', tone: 'danger' };
    if (this.exportedFiles && this.exportedFiles.length > 0) return { label: 'Completed', tone: 'success' };
    return { label: 'Optional', tone: 'muted' };
  }

  get guidanceText(): string {
    if (this.isDemo) return 'Demo mode is read-only. Uploads and processing are disabled.';
    if (!this.files.roverObsName) {
      return 'Upload Rover Data first (.ubx/.dat recommended, or .obs). If you upload .ubx/.dat, we will automatically generate rover.obs and rover.nav for you.';
    }
    if (!this.files.baseObsName) {
      return 'Upload Base Data next (.rtcm3/.dat recommended, or .obs). If you upload .rtcm3/.dat, we will automatically generate base.obs for you.';
    }
    if (!this.files.roverNavName) {
      if (this.roverInputKind === 'obs') {
        return 'Upload Navigation Data (rover.nav). This is required because you uploaded a Rover .obs file.';
      }
      return 'Please wait: rover.nav is being generated automatically. You can click Refresh to check again.';
    }
    if (!this.files.resultPosUrl) {
      return 'Click Process to run PPK in the backend. You can leave this page and come back later—processing will continue.';
    }
    if (!this.exportedFiles || this.exportedFiles.length === 0) {
      return 'Optional: Click Export To to generate CSV/KML/KMZ/GPX/SHP from result.pos.';
    }
    return 'Exported files are ready. Click a file name to download, or use the trash icon to delete.';
  }

  get nextStepNumber(): 1 | 2 | 3 | 4 | 5 {
    if (!this.files.roverObsName) return 1;
    if (!this.files.baseObsName) return 2;
    if (!this.files.roverNavName) return 3;
    if (!this.files.resultPosUrl) return 4;
    return 5;
  }

  get nextStepShortText(): string {
    if (this.isDemo) return 'Demo mode is read-only.';
    if (this.allStepsCompleted) return 'All steps completed. Download exported files if needed.';
    switch (this.nextStepNumber) {
      case 1:
        return 'Upload Rover Data';
      case 2:
        return 'Upload Base Data';
      case 3:
        return (this.roverInputKind === 'obs') ? 'Upload Navigation Data (rover.nav)' : 'Wait for Navigation (auto-generated)';
      case 4:
        return 'Click Process';
      case 5:
        return (this.exportedFiles && this.exportedFiles.length > 0) ? 'Download exported files (optional)' : 'Click Export To (optional)';
    }
  }

  get allStepsCompleted(): boolean {
    return !!this.files.resultPosUrl && Array.isArray(this.exportedFiles) && this.exportedFiles.length > 0;
  }

  private maxUploadBytes(): number {
    // downloader-pro: 1GB, otherwise 100MB
    return this.isDownloaderPro ? 1024 * 1024 * 1024 : 100 * 1024 * 1024;
  }

  private validateUploadFileSize(file: File): boolean {
    const max = this.maxUploadBytes();
    if (file.size > max) {
      const mb = (max / (1024 * 1024)).toFixed(0);
      this.snackBar.open(`File is too large. Max allowed is ${mb}MB.`, 'Close', { duration: 5000 });
      return false;
    }
    return true;
  }

  ngOnInit(): void {
    this.loadInitialState();
    this.loadExportState();
  }

  ngOnDestroy(): void {
    if (this.statusPollHandle) {
      clearInterval(this.statusPollHandle);
      this.statusPollHandle = null;
    }
    if (this.exportPollHandle) {
      clearInterval(this.exportPollHandle);
      this.exportPollHandle = null;
    }
    if (this.postUploadRoverPollHandle) {
      clearInterval(this.postUploadRoverPollHandle);
      this.postUploadRoverPollHandle = null;
    }
    if (this.postUploadBasePollHandle) {
      clearInterval(this.postUploadBasePollHandle);
      this.postUploadBasePollHandle = null;
    }
  }

  get canProcess(): boolean {
    return !!this.files.roverObsName && !!this.files.baseObsName && !!this.files.roverNavName && !this.isDemo;
  }

  // ---------- Initial load ----------

  loadInitialState(): void {
    this.ppk.getStatus().subscribe({
      next: (res) => this.applyStatusResponse(res),
      error: () => {
        this.statusText = 'Failed to load initial PPK status.';
      }
    });
  }

  private startPostUploadPolling(kind: 'rover' | 'base'): void {
    const intervalMs = 3000;
    const maxMs = 10_000;
    const warnMs = 10_000;
    const startedAt = Date.now();

    const done = () => {
      if (kind === 'rover' && this.postUploadRoverPollHandle) {
        clearInterval(this.postUploadRoverPollHandle);
        this.postUploadRoverPollHandle = null;
      }
      if (kind === 'base' && this.postUploadBasePollHandle) {
        clearInterval(this.postUploadBasePollHandle);
        this.postUploadBasePollHandle = null;
      }
    };

    // avoid duplicate polling
    if (kind === 'rover' && this.postUploadRoverPollHandle) return;
    if (kind === 'base' && this.postUploadBasePollHandle) return;
    if (kind === 'rover') this.postUploadRoverWarned = false;
    if (kind === 'base') this.postUploadBaseWarned = false;

    const tick = () => {
      // stop if already satisfied
      if (kind === 'rover') {
        if (this.files.roverObsUrl && this.files.roverNavUrl) { done(); return; }
      } else {
        if (this.files.baseObsUrl) { done(); return; }
      }

      const elapsed = Date.now() - startedAt;
      if (elapsed > warnMs) {
        if (kind === 'rover' && !this.postUploadRoverWarned) {
          this.postUploadRoverWarned = true;
          this.snackBar.open('Rover conversion is still running. Please wait and click Refresh or Retry later.', 'Close', { duration: 6000 });
        }
        if (kind === 'base' && !this.postUploadBaseWarned) {
          this.postUploadBaseWarned = true;
          this.snackBar.open('Base conversion is still running. Please wait and click Refresh or Retry later.', 'Close', { duration: 6000 });
        }
      }
      if (elapsed > maxMs) {
        done();
        if (kind === 'rover') {
          this.snackBar.open('Rover conversion is still running. Please wait and click Refresh later.', 'Close', { duration: 6000 });
        } else {
          this.snackBar.open('Base conversion is still running. Please wait and click Refresh later.', 'Close', { duration: 6000 });
        }
        return;
      }

      this.ppk.getStatus().subscribe({
        next: res => this.applyStatusResponse(res),
        error: () => { /* ignore transient */ }
      });
    };

    // immediate + interval
    tick();
    const handle = setInterval(tick, intervalMs);
    if (kind === 'rover') this.postUploadRoverPollHandle = handle;
    if (kind === 'base') this.postUploadBasePollHandle = handle;
  }

  private applyConvertResponse(res: PpkConvertFilesResponse | null | undefined): void {
    const status = (res?.status || 'IDLE').toUpperCase() as any;
    if (status === 'RUNNING' || status === 'SUCCESS' || status === 'FAILED' || status === 'IDLE') {
      this.exportStatus = status;
    } else {
      this.exportStatus = 'IDLE';
    }
    this.exportStatusText = res?.message || (this.exportStatus === 'RUNNING' ? 'Export in progress...' : '');
    this.exportedFiles = (res?.files || []) as PpkConvertFile[];
  }

  loadExportState(): void {
    this.ppk.getConvertFiles().subscribe({
      next: res => this.applyConvertResponse(res),
      error: () => {
        // keep silent; export is optional
      }
    });
  }

  private applyStatusResponse(res: PpkStatusResponse | null | undefined): void {
    const files = res?.files || {};
    this.files.roverObsUrl = files.roverObsUrl;
    this.files.roverObsName = files.roverObsUrl ? this.extractFileName(files.roverObsUrl) : undefined;
    
    this.files.baseObsUrl = files.baseObsUrl;
    this.files.baseObsName = files.baseObsUrl ? this.extractFileName(files.baseObsUrl) : undefined;
    
    this.files.roverNavUrl = files.roverNavUrl;
    this.files.roverNavName = files.roverNavUrl ? this.extractFileName(files.roverNavUrl) : undefined;
    
    this.files.resultPosUrl = files.resultPosUrl || undefined;

    if (res?.status) {
      const st = String(res.status || '').toUpperCase();
      if (st === 'RUNNING' || st === 'SUCCESS' || st === 'FAILED' || st === 'IDLE') {
        this.ppkRunStatus = st as any;
      } else {
        this.ppkRunStatus = '';
      }
      this.statusText = res.status === 'SUCCESS'
        ? 'PPK processing completed.'
        : res.status === 'RUNNING'
          ? 'PPK processing in progress...'
          : res.status === 'FAILED'
            ? (res.message || 'PPK processing failed.')
            : res.status;
    } else {
      this.ppkRunStatus = 'IDLE';
      this.statusText = '';
    }
  }

  private extractFileName(url: string): string {
    try {
      const u = new URL(url);
      const path = u.pathname || '';
      const idx = path.lastIndexOf('/');
      return idx >= 0 ? path.substring(idx + 1) : path || url;
    } catch {
      const idx = url.lastIndexOf('/');
      return idx >= 0 ? url.substring(idx + 1) : url;
    }
  }

  // ---------- Upload handlers ----------

  onUploadRover(): void {
    if (this.isDemo) { this.readOnlyNotice(); return; }
    this.pickFile(['.ubx', '.dat', '.obs']).then(file => {
      if (!file) { return; }
      if (!this.validateUploadFileSize(file)) { return; }
      const lower = file.name.toLowerCase();
      // .dat is treated as UBX (same payload, different extension)
      const kind = (lower.endsWith('.ubx') || lower.endsWith('.dat')) ? 'ubx' : 'obs';
      this.roverInputKind = kind;
      this.roverLoading = true;
      this.roverProgress = 0;
      this.ppk.getRoverUploadUrl(kind as any).subscribe({
        next: cfg => {
          if (!cfg.ok || !cfg.uploadUrl) {
            this.roverLoading = false;
            this.roverProgress = null;
            this.snackBar.open(cfg.message || 'Failed to get upload URL.', 'Close', { duration: 4000 });
            return;
          }
          this.uploader.putWithProgress(cfg.uploadUrl, file).subscribe({
            next: prog => {
              this.roverProgress = prog.progress;
              if (prog.done) {
                this.ppk.notifyRover(kind as any).subscribe({
                  next: res => {
                    this.roverLoading = false;
                    this.roverProgress = null;
                    if (!res.ok) {
                      this.snackBar.open(res.message || 'Failed to process rover file.', 'Close', { duration: 4000 });
                      return;
                    }
                    const files = res.files || {};
                    if (files.roverObsUrl) {
                      this.files.roverObsUrl = files.roverObsUrl;
                      this.files.roverObsName = this.extractFileName(files.roverObsUrl);
                    }
                    if (files.roverNavUrl) {
                      this.files.roverNavUrl = files.roverNavUrl;
                      this.files.roverNavName = this.extractFileName(files.roverNavUrl);
                    }
                    // For UBX uploads, rover.obs/rover.nav are produced asynchronously by convbin.
                    // If not present yet, poll status (max 60s, every 3s).
                    if (kind === 'ubx') {
                      this.startPostUploadPolling('rover');
                    } else if (!this.files.roverObsUrl) {
                      this.snackBar.open('Uploaded rover file is invalid or could not be processed.', 'Close', { duration: 5000 });
                    }
                  },
                  error: err => {
                    this.roverLoading = false;
                    this.roverProgress = null;
                    const msg = err?.error?.message || err?.message || 'Failed to process rover file.';
                    this.snackBar.open(msg, 'Close', { duration: 4000 });
                  }
                });
              }
            },
            error: err => {
              this.roverLoading = false;
              this.roverProgress = null;
              const msg = err?.error?.message || err?.message || 'Failed to upload to S3.';
              this.snackBar.open(msg, 'Close', { duration: 4000 });
            }
          });
        },
        error: err => {
          this.roverLoading = false;
          this.roverProgress = null;
          const msg = err?.error?.message || err?.message || 'Failed to get upload URL.';
          this.snackBar.open(msg, 'Close', { duration: 4000 });
        }
      });
    });
  }

  onUploadBase(): void {
    if (this.isDemo) { this.readOnlyNotice(); return; }
    this.pickFile(['.rtcm3', '.dat', '.obs']).then(file => {
      if (!file) { return; }
      if (!this.validateUploadFileSize(file)) { return; }
      const lower = file.name.toLowerCase();
      // .dat is treated as RTCM3 (same payload, different extension)
      const kind = (lower.endsWith('.rtcm3') || lower.endsWith('.dat')) ? 'rtcm3' : 'obs';
      this.baseInputKind = kind;
      const baseStartDate: string | null = null;
      this.baseLoading = true;
      this.baseProgress = 0;
      this.ppk.getBaseUploadUrl(kind as any, baseStartDate || undefined).subscribe({
        next: cfg => {
          if (!cfg.ok || !cfg.uploadUrl) {
            this.baseLoading = false;
            this.baseProgress = null;
            this.snackBar.open(cfg.message || 'Failed to get upload URL.', 'Close', { duration: 4000 });
            return;
          }
          this.uploader.putWithProgress(cfg.uploadUrl, file).subscribe({
            next: prog => {
              this.baseProgress = prog.progress;
              if (prog.done) {
                this.ppk.notifyBase(kind as any, baseStartDate || undefined).subscribe({
                  next: res => {
                    this.baseLoading = false;
                    this.baseProgress = null;
                    if (!res.ok) {
                      this.snackBar.open(res.message || 'Failed to process base file.', 'Close', { duration: 4000 });
                      return;
                    }
                    const files = res.files || {};
                    if (files.baseObsUrl) {
                      this.files.baseObsUrl = files.baseObsUrl;
                      this.files.baseObsName = this.extractFileName(files.baseObsUrl);
                    }
                    // For RTCM3 uploads, base.obs is produced asynchronously by convbin.
                    if (kind === 'rtcm3') {
                      this.startPostUploadPolling('base');
                    } else if (!this.files.baseObsUrl) {
                      this.snackBar.open('Uploaded base file is invalid or could not be processed.', 'Close', { duration: 5000 });
                    }
                  },
                  error: err => {
                    this.baseLoading = false;
                    this.baseProgress = null;
                    const msg = err?.error?.message || err?.message || 'Failed to process base file.';
                    this.snackBar.open(msg, 'Close', { duration: 4000 });
                  }
                });
              }
            },
            error: err => {
              this.baseLoading = false;
              this.baseProgress = null;
              const msg = err?.error?.message || err?.message || 'Failed to upload to S3.';
              this.snackBar.open(msg, 'Close', { duration: 4000 });
            }
          });
        },
        error: err => {
          this.baseLoading = false;
          this.baseProgress = null;
          const msg = err?.error?.message || err?.message || 'Failed to get upload URL.';
          this.snackBar.open(msg, 'Close', { duration: 4000 });
        }
      });
    });
  }

  onUploadNavigation(): void {
    if (this.isDemo) { this.readOnlyNotice(); return; }
    this.pickFile(['.nav']).then(file => {
      if (!file) { return; }
      if (!this.validateUploadFileSize(file)) { return; }
      this.navLoading = true;
      this.navProgress = 0;
      this.ppk.getNavUploadUrl().subscribe({
        next: cfg => {
          if (!cfg.ok || !cfg.uploadUrl) {
            this.navLoading = false;
            this.navProgress = null;
            this.snackBar.open(cfg.message || 'Failed to get upload URL.', 'Close', { duration: 4000 });
            return;
          }
          this.uploader.putWithProgress(cfg.uploadUrl, file).subscribe({
            next: prog => {
              this.navProgress = prog.progress;
              if (prog.done) {
                this.ppk.notifyNav().subscribe({
                  next: res => {
                    this.navLoading = false;
                    this.navProgress = null;
                    if (!res.ok) {
                      this.snackBar.open(res.message || 'Failed to process navigation file.', 'Close', { duration: 4000 });
                      return;
                    }
                    const files = res.files || {};
                    if (files.roverNavUrl) {
                      this.files.roverNavUrl = files.roverNavUrl;
                      this.files.roverNavName = this.extractFileName(files.roverNavUrl);
                    }
                    if (!this.files.roverNavUrl) {
                      this.snackBar.open('Uploaded navigation file is invalid or could not be processed.', 'Close', { duration: 5000 });
                    }
                  },
                  error: err => {
                    this.navLoading = false;
                    this.navProgress = null;
                    const msg = err?.error?.message || err?.message || 'Failed to process navigation file.';
                    this.snackBar.open(msg, 'Close', { duration: 4000 });
                  }
                });
              }
            },
            error: err => {
              this.navLoading = false;
              this.navProgress = null;
              const msg = err?.error?.message || err?.message || 'Failed to upload to S3.';
              this.snackBar.open(msg, 'Close', { duration: 4000 });
            }
          });
        },
        error: err => {
          this.navLoading = false;
          this.navProgress = null;
          const msg = err?.error?.message || err?.message || 'Failed to get upload URL.';
          this.snackBar.open(msg, 'Close', { duration: 4000 });
        }
      });
    });
  }

  onSelectDownloaderFile(): void {
    if (this.isDemo) {
      this.readOnlyNotice();
      return;
    }

    const dialogRef = this.dialog.open(SelectDownloaderFileDialogComponent, {
      width: '800px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: DownloaderFile | undefined) => {
      if (result) {
        this.copyDownloaderFile(result.mountpoint);
      }
    });
  }

  private copyDownloaderFile(mountpoint: string): void {
    this.baseLoading = true;
    this.ppk.copyDownloaderFile(mountpoint).subscribe({
      next: (res) => {
        this.baseLoading = false;
        if (res.ok) {
          this.applyStatusResponse(res);
          this.snackBar.open('File copied and processed successfully', 'Close', { duration: 3000 });
        } else {
          this.snackBar.open(res.message || 'Copy failed', 'Close', { duration: 3000 });
        }
      },
      error: (err) => {
        this.baseLoading = false;
        console.error(err);
        this.snackBar.open('Failed to copy file', 'Close', { duration: 3000 });
      }
    });
  }

  // ---------- Process / status poll ----------

  onProcess(): void {
    if (this.isDemo) {
      this.readOnlyNotice();
      return;
    }
    if (!this.canProcess) {
      return;
    }

    const startAfterCleanup = () => {
      // Auto-delete previous result if exists
      if (this.files.resultPosUrl) {
        this.statusText = 'Cleaning up previous result...';
        this.processLoading = true;
        this.ppk.deleteResult().subscribe({
          next: (res) => {
            // Update UI to remove result file
            this.applyStatusResponse(res);
            // Proceed to start processing
            this.doStartProcess();
          },
          error: () => {
            // Backend will overwrite result anyway; proceed.
            this.doStartProcess();
          }
        });
      } else {
        this.doStartProcess();
      }
    };

    // If there are any exported files, delete them before starting a new PPK run.
    if (Array.isArray(this.exportedFiles) && this.exportedFiles.length > 0) {
      this.statusText = 'Cleaning up exported files...';
      this.processLoading = true;
      this.ppk.deleteAllConvertFiles().subscribe({
        next: (res) => {
          if (!res.ok) {
            this.snackBar.open(res.message || 'Failed to delete exported files.', 'Close', { duration: 4000 });
          }
          // Clear UI state optimistically; backend will also clear on start as a safeguard.
          this.exportedFiles = [];
          this.exportStatus = 'IDLE';
          this.exportStatusText = '';
          startAfterCleanup();
        },
        error: (err) => {
          const msg = err?.error?.message || err?.message || 'Failed to delete exported files.';
          this.snackBar.open(msg, 'Close', { duration: 4000 });
          // Proceed; backend will also clear on start as a safeguard.
          startAfterCleanup();
        }
      });
      return;
    }

    startAfterCleanup();
  }

  private doStartProcess(): void {
    this.processLoading = true;
    this.statusText = 'Starting PPK processing...';
    this.ppkRunStatus = 'RUNNING';
    
    // Clear current result UI immediately (optimistic update)
    this.files.resultPosUrl = undefined;

    this.ppk.startRnx2rtkp().subscribe({
      next: res => {
        this.processLoading = false;
        if (!res.ok) {
          this.snackBar.open(res.message || 'Failed to start PPK processing.', 'Close', { duration: 4000 });
          this.statusText = 'Failed to start processing.';
          return;
        }
        const timestamp = new Date().toLocaleTimeString();
        this.statusText = `PPK processing started at ${timestamp}. It will take some time to complete. You can safely leave this page and check back later.`;
        
        // Refresh status after 2 seconds to confirm result.pos is gone and sync state
        setTimeout(() => {
          this.loadInitialState();
          this.startStatusPolling();
        }, 2000);
      },
      error: err => {
        this.processLoading = false;
        const msg = err?.error?.message || err?.message || 'Failed to start PPK processing.';
        this.snackBar.open(msg, 'Close', { duration: 4000 });
        this.statusText = 'Error starting processing.';
      }
    });
  }

  onRefreshStatus(): void {
    this.ppk.getStatus().subscribe({
      next: res => this.applyStatusResponse(res),
      error: () => {
        this.snackBar.open('Failed to refresh PPK status.', 'Close', { duration: 3000 });
      }
    });
    this.loadExportState();
  }

  private startStatusPolling(): void {
    if (this.statusPollHandle) {
      clearInterval(this.statusPollHandle);
      this.statusPollHandle = null;
    }
    this.statusPollHandle = setInterval(() => {
      this.ppk.getStatus().subscribe({
        next: res => {
          this.applyStatusResponse(res);
          if (res?.status && (res.status === 'SUCCESS' || res.status === 'FAILED')) {
            if (this.statusPollHandle) {
              clearInterval(this.statusPollHandle);
              this.statusPollHandle = null;
            }
          }
        },
        error: () => { /* ignore transient errors during polling */ }
      });
    }, 8000);
  }

  // ---------- Export / convert ----------

  onExportTo(): void {
    if (this.isDemo) {
      this.readOnlyNotice();
      return;
    }
    if (!this.files.resultPosUrl) {
      return;
    }
    if (this.exportStatus === 'RUNNING' || this.exportLoading) {
      return;
    }

    this.exportLoading = true;
    this.exportStatusText = 'Starting export...';
    this.ppk.startConvertFiles().subscribe({
      next: res => {
        this.exportLoading = false;
        if (!res.ok) {
          this.snackBar.open(res.message || 'Failed to start export.', 'Close', { duration: 4000 });
          this.exportStatusText = 'Failed to start export.';
          return;
        }
        this.exportStatus = 'RUNNING';
        this.exportStatusText = 'Export started. Generating files...';
        this.startExportPolling();
      },
      error: err => {
        this.exportLoading = false;
        const code = err?.status;
        if (code === 409) {
          this.exportStatus = 'RUNNING';
          this.exportStatusText = err?.error?.message || 'Export is already running.';
          this.startExportPolling();
          return;
        }
        const msg = err?.error?.message || err?.message || 'Failed to start export.';
        this.snackBar.open(msg, 'Close', { duration: 4000 });
        this.exportStatusText = msg;
      }
    });
  }

  private startExportPolling(): void {
    if (this.exportPollHandle) {
      clearInterval(this.exportPollHandle);
      this.exportPollHandle = null;
    }
    this.exportPollStartAt = Date.now();

    const pollOnce = () => {
      const elapsed = Date.now() - this.exportPollStartAt;
      if (elapsed > 120_000) {
        if (this.exportPollHandle) {
          clearInterval(this.exportPollHandle);
          this.exportPollHandle = null;
        }
        // Keep RUNNING status; user must wait and refresh later.
        this.exportStatus = 'RUNNING';
        this.exportStatusText = 'Export is still running. Please wait and refresh later.';
        return;
      }
      this.ppk.getConvertFiles().subscribe({
        next: res => {
          this.applyConvertResponse(res);
          if (this.exportStatus === 'SUCCESS' || this.exportStatus === 'FAILED') {
            if (this.exportPollHandle) {
              clearInterval(this.exportPollHandle);
              this.exportPollHandle = null;
            }
          }
        },
        error: () => {
          // ignore transient errors
        }
      });
    };

    // immediate poll, then every 5 seconds
    pollOnce();
    this.exportPollHandle = setInterval(pollOnce, 5000);
  }

  onDeleteExportFile(name: string): void {
    if (this.isDemo) { this.readOnlyNotice(); return; }
    if (!name) { return; }
    if (!confirm(`Delete exported file "${name}"?`)) return;
    this.ppk.deleteConvertFile(name).subscribe({
      next: res => {
        if (!res.ok) {
          this.snackBar.open(res.message || 'Failed to delete file.', 'Close', { duration: 3000 });
          return;
        }
        this.loadExportState();
      },
      error: err => {
        const msg = err?.error?.message || err?.message || 'Failed to delete file.';
        this.snackBar.open(msg, 'Close', { duration: 3000 });
      }
    });
  }

  onDeleteAllExportFiles(): void {
    if (this.isDemo) { this.readOnlyNotice(); return; }
    if (!confirm('Delete all exported files?')) return;
    this.ppk.deleteAllConvertFiles().subscribe({
      next: res => {
        if (!res.ok) {
          this.snackBar.open(res.message || 'Failed to delete exported files.', 'Close', { duration: 3000 });
          return;
        }
        this.loadExportState();
      },
      error: err => {
        const msg = err?.error?.message || err?.message || 'Failed to delete exported files.';
        this.snackBar.open(msg, 'Close', { duration: 3000 });
      }
    });
  }

  // ---------- Helpers ----------

  private async pickFile(acceptExt: string[]): Promise<File | null> {
    return new Promise<File | null>((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = acceptExt.join(',');
      input.onchange = () => {
        const file = input.files && input.files.length > 0 ? input.files[0] : null;
        resolve(file);
      };
      input.click();
    });
  }

  private readOnlyNotice(): void {
    this.dialog.open(AcknowledgeDialogComponent, {
      data: 'You have read-only access, but not permission to make changes.'
    });
  }

  // ---------- Deletion ----------

  onDeleteRover(): void {
    if (this.isDemo) { this.readOnlyNotice(); return; }
    if (!confirm('Are you sure you want to delete the Rover data?')) return;
    this.roverLoading = true;
    this.ppk.deleteRover().subscribe({
      next: res => {
        this.roverLoading = false;
        if (res.ok) {
          this.applyStatusResponse(res);
          this.snackBar.open('Rover data deleted.', 'Close', { duration: 3000 });
        } else {
          this.snackBar.open(res.message || 'Failed to delete rover data.', 'Close', { duration: 4000 });
        }
      },
      error: err => {
        this.roverLoading = false;
        this.snackBar.open('Failed to delete rover data.', 'Close', { duration: 4000 });
      }
    });
  }

  onDeleteBase(): void {
    if (this.isDemo) { this.readOnlyNotice(); return; }
    if (!confirm('Are you sure you want to delete the Base data?')) return;
    this.baseLoading = true;
    this.ppk.deleteBase().subscribe({
      next: res => {
        this.baseLoading = false;
        if (res.ok) {
          this.applyStatusResponse(res);
          this.snackBar.open('Base data deleted.', 'Close', { duration: 3000 });
        } else {
          this.snackBar.open(res.message || 'Failed to delete base data.', 'Close', { duration: 4000 });
        }
      },
      error: err => {
        this.baseLoading = false;
        this.snackBar.open('Failed to delete base data.', 'Close', { duration: 4000 });
      }
    });
  }

  onDeleteNav(): void {
    if (this.isDemo) { this.readOnlyNotice(); return; }
    if (!confirm('Are you sure you want to delete the Navigation data?')) return;
    this.navLoading = true;
    this.ppk.deleteNav().subscribe({
      next: res => {
        this.navLoading = false;
        if (res.ok) {
          this.applyStatusResponse(res);
          this.snackBar.open('Navigation data deleted.', 'Close', { duration: 3000 });
        } else {
          this.snackBar.open(res.message || 'Failed to delete navigation data.', 'Close', { duration: 4000 });
        }
      },
      error: err => {
        this.navLoading = false;
        this.snackBar.open('Failed to delete navigation data.', 'Close', { duration: 4000 });
      }
    });
  }

  onDeleteResult(): void {
    if (this.isDemo) { this.readOnlyNotice(); return; }
    if (!confirm('Are you sure you want to delete the result file?')) return;
    this.ppk.deleteResult().subscribe({
      next: res => {
        if (res.ok) {
          this.applyStatusResponse(res);
          // result.pos-derived exported files become stale; clear UI and refresh export state
          this.exportedFiles = [];
          this.exportStatus = 'IDLE';
          this.exportStatusText = '';
          this.loadExportState();
          this.snackBar.open('Result file deleted.', 'Close', { duration: 3000 });
        } else {
          this.snackBar.open(res.message || 'Failed to delete result file.', 'Close', { duration: 4000 });
        }
      },
      error: err => {
        this.snackBar.open('Failed to delete result file.', 'Close', { duration: 4000 });
      }
    });
  }
}


